import mixpanel from 'mixpanel-browser'
import posthog from 'posthog-js'
import { type Plugin } from 'vue'

import { FeatureFlags } from '@/plugins/featureFlags'

const VITE_DEBUG_ANALYTICS = import.meta.env.VITE_DEBUG_ANALYTICS
const VITE_MIXPANEL_PROJECT = import.meta.env.VITE_MIXPANEL_PROJECT
const VITE_POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const VITE_POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST

const isMixpanelEnabled =
  (VITE_DEBUG_ANALYTICS === 'true' && !!VITE_MIXPANEL_PROJECT) ||
  (import.meta.env.MODE === 'production' && VITE_MIXPANEL_PROJECT)

const isPostHogEnabled =
  (VITE_DEBUG_ANALYTICS === 'true' && !!VITE_POSTHOG_KEY) ||
  (import.meta.env.MODE !== 'development' && !!VITE_POSTHOG_KEY)

const isHubSpotEnabled =
  VITE_DEBUG_ANALYTICS === 'true' || import.meta.env.MODE === 'production'

// Declare HubSpot types for TypeScript
declare global {
  interface Window {
    _hsq?: unknown[]
  }
}

/**
 * Track an analytics event
 * @param eventName Name of event to track
 * @param [args] Extra properties to pass with the event
 */
const track = (eventName: string, args: Record<string, unknown> = {}): void => {
  // Track with Mixpanel
  if (
    isMixpanelEnabled &&
    !FeatureFlags.isEnabled('hermes.stopMixpanelEmission')
  ) {
    mixpanel.track(eventName, args)
  }

  // Track with PostHog
  if (isPostHogEnabled) {
    posthog.capture(eventName, args)
  }

  // Track with HubSpot using the _hsq queue
  if (isHubSpotEnabled && window._hsq) {
    window._hsq.push([
      'trackEvent',
      {
        id: eventName,
        ...args,
      },
    ])
  }
}

/**
 * Identify User
 * @param userId Id Of User
 * @param [properties] Additional user properties
 */
const identify = (
  userId: string,
  properties: Record<string, unknown> = {}
): void => {
  // Identify with Mixpanel
  if (isMixpanelEnabled) {
    mixpanel.identify(userId)
  }

  // Identify with PostHog
  if (isPostHogEnabled) {
    posthog.identify(userId, properties)
  }

  // Identify with HubSpot using the _hsq queue
  if (isHubSpotEnabled && window._hsq) {
    const email = (properties.email as string) || userId

    window._hsq.push([
      'identify',
      {
        email,
        ...properties,
      },
    ])
  }
}

let prevPage: string | undefined

/**
 * Abstracts `track` to manage page loads.
 * Dedupes page load calls
 * @param pageName Name of page being loaded
 */
export const sendPageLoad = (pageName: string): void => {
  if (prevPage !== pageName) {
    prevPage = pageName

    track('Page Load', {
      pageName,
    })
  }
}

/**
 * Reset user id (logout)
 */
const reset = (): void => {
  // Reset Mixpanel
  if (isMixpanelEnabled) {
    mixpanel.reset()
  }

  // Reset PostHog
  if (isPostHogEnabled) {
    posthog.reset()
  }

  // Reset HubSpot (clear tracking cookies)
  if (isHubSpotEnabled && window._hsq) {
    window._hsq.push(['reset'])
  }
}

/**
 * Sets specific data about user
 * @param data Data to set
 */
const setUserData = (data: Record<string, unknown>): void => {
  // Set user data in Mixpanel
  if (isMixpanelEnabled) {
    mixpanel.people.set({
      ...data,
      environment: import.meta.env.MODE,
    })
  }

  // Set user data in PostHog
  if (isPostHogEnabled) {
    posthog.setPersonProperties({
      ...data,
      environment: import.meta.env.MODE,
    })
  }

  // Set user data in HubSpot using identify
  if (isHubSpotEnabled && window._hsq) {
    const hubspotData = {
      ...data,
      environment: import.meta.env.MODE,
    }

    window._hsq.push(['identify', hubspotData])
  }
}

export const analytics = {
  identify,
  reset,
  setUserData,
  track,
}

export const AnalyticsPlugin: Plugin = {
  install: function (app) {
    const debug =
      import.meta.env.MODE !== 'production' && VITE_DEBUG_ANALYTICS === 'true'

    // Initialize Mixpanel
    if (isMixpanelEnabled) {
      mixpanel.init(VITE_MIXPANEL_PROJECT, {
        debug,
      })
      mixpanel.register({
        application: 'hermes',
      })
    }

    // Initialize PostHog
    if (isPostHogEnabled) {
      posthog.init(VITE_POSTHOG_KEY, {
        api_host: VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
        capture_pageleave: false,
        capture_pageview: false,
        debug,
      })
      posthog.register({
        application: 'hermes',
      })
    }

    // Initialize HubSpot tracking queue
    if (isHubSpotEnabled) {
      window._hsq = window._hsq || []

      // Set global properties for HubSpot
      window._hsq.push(['setPath', window.location.pathname])
      window._hsq.push(['trackPageView'])
    }

    app.config.globalProperties.$analytics = analytics
  },
}
